/**
 * VideoOrderModal
 *
 * Shown after the player selects more than one video. Lists the files and lets
 * the player drag to reorder them before confirming. The confirmed order is
 * passed back to the caller, which initiates the upload-and-stitch flow.
 *
 * Design follows the shared iOS grouped-list recipe: #F2F2F7 surface, white
 * rows, 1px #e6e6e6 separator, radius-14 panel, FM-5 capsule buttons.
 * Drag handle uses the standard 6-dot grip icon. A drag-over target highlights
 * with a 2px biscayne border so the drop zone is unambiguous.
 */

import { useState, useRef } from 'react';
import type { SelectedCloudFile } from './CloudFilePickerModal';
import { VideoThumb } from './VideoThumb';

// ── Types ─────────────────────────────────────────────────────────────────────

export type MultiFileItem =
  | { type: 'local'; file: File }
  | { type: 'cloud'; file: SelectedCloudFile };

// ── Helpers ───────────────────────────────────────────────────────────────────

function itemDisplayName(item: MultiFileItem): string {
  if (item.type === 'local') return item.file.name;
  return item.file.displayName.replace(/\s+—\s+/g, ' · ');
}

// ── Shared recipes ────────────────────────────────────────────────────────────

const GROUP = 'bg-white rounded-[12px] overflow-hidden';

const BTN_BASE =
  'w-full h-12 rounded-[100px] inline-flex items-center justify-center gap-[6px] ' +
  'text-[17px] leading-[22px] font-medium tracking-[-0.43px] ' +
  'disabled:opacity-40 disabled:cursor-not-allowed ios-press';
const BTN_PRIMARY = `${BTN_BASE} bg-biscayne text-white`;
const BTN_SECONDARY = `${BTN_BASE} bg-[rgba(120,120,128,0.16)] text-black`;

// ── Drag handle icon ─────────────────────────────────────────────────────────

function DragHandle() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="rgba(60,60,67,0.4)"
      aria-hidden="true"
      className="flex-shrink-0 cursor-grab active:cursor-grabbing"
    >
      {/* 3×2 dot grid */}
      <circle cx="7" cy="6" r="1.5" />
      <circle cx="13" cy="6" r="1.5" />
      <circle cx="7" cy="10" r="1.5" />
      <circle cx="13" cy="10" r="1.5" />
      <circle cx="7" cy="14" r="1.5" />
      <circle cx="13" cy="14" r="1.5" />
    </svg>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────

function FileRow({
  item,
  index,
  isDragOver,
  isDragging,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: {
  item: MultiFileItem;
  index: number;
  isDragOver: boolean;
  isDragging: boolean;
  onDragStart: (index: number) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (index: number) => void;
  onDragEnd: () => void;
}) {
  const name = itemDisplayName(item);

  return (
    <div
      draggable
      data-testid="video-order-row"
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={() => onDrop(index)}
      onDragEnd={onDragEnd}
      className="pl-4 first:[&>div]:border-t-0 transition-opacity"
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <div
        className="border-t border-[#e6e6e6]"
        style={isDragOver ? { borderTopColor: '#0088ff', borderTopWidth: 2 } : undefined}
      >
        <div className="flex items-center gap-3 pr-3" style={{ height: 64 }}>
          {/* Drag handle */}
          <DragHandle />

          {/* Frame from the video, or a play tile when none is available */}
          <VideoThumb item={item} />

          {/* File name + source badge */}
          <div className="flex-1 min-w-0">
            <div className="[overflow-wrap:anywhere] text-[15px] leading-[20px] tracking-[-0.43px] text-black">
              {name}
            </div>
            <div className="text-[11px] leading-[16px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 2 }}>
              {item.type === 'local' ? 'From device' : 'From Double Bagel Cloud'}
            </div>
          </div>

          {/* Index badge */}
          <div
            className="flex-shrink-0 flex items-center justify-center text-[13px] font-semibold text-[rgba(60,60,67,0.5)]"
            style={{ width: 24, height: 24, borderRadius: 12, background: 'rgba(120,120,128,0.12)' }}
          >
            {index + 1}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface VideoOrderModalProps {
  items: MultiFileItem[];
  onConfirm: (ordered: MultiFileItem[]) => void;
  onCancel: () => void;
}

export function VideoOrderModal({ items, onConfirm, onCancel }: VideoOrderModalProps) {
  const [order, setOrder] = useState<MultiFileItem[]>(items);
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
    setDraggingIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndexRef.current !== null && dragIndexRef.current !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (toIndex: number) => {
    const fromIndex = dragIndexRef.current;
    if (fromIndex === null || fromIndex === toIndex) return;
    const next = [...order];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setOrder(next);
    dragIndexRef.current = null;
    setDragOverIndex(null);
    setDraggingIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragOverIndex(null);
    setDraggingIndex(null);
  };

  const confirmLabel =
    order.length === 2
      ? 'Stitch 2 videos together'
      : `Stitch ${order.length} videos together`;

  return (
    <div
      className="fixed inset-0 flex items-end justify-center px-2 sm:items-center sm:px-0"
      style={{
        zIndex: 10000,
        background: 'rgba(0,0,0,0.4)',
        paddingBottom: 'max(8px, env(safe-area-inset-bottom))',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Arrange your videos"
        data-testid="video-order-modal"
        className="flex flex-col w-full sm:w-[480px]"
        style={{
          maxHeight: '85vh',
          background: '#F2F2F7',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between flex-shrink-0"
          style={{ padding: '20px 16px 0' }}
        >
          <div>
            <h2 className="text-[22px] leading-[28px] font-semibold tracking-[-0.26px] text-black">
              Arrange your videos
            </h2>
            <p className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 4 }}>
              Drag to reorder. They will be stitched in this order.
            </p>
          </div>
          <button
            onClick={onCancel}
            aria-label="Cancel"
            className="relative after:absolute after:-inset-1 after:content-[''] ios-press flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
            style={{ marginRight: -4, marginTop: 4, border: 'none', background: 'rgba(120,120,128,0.16)' }}
          >
            <svg width="18" height="18" fill="none" stroke="#1C1C1C" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* File list */}
        <div
          className="flex-1 overflow-y-auto min-h-0"
          style={{ padding: '16px 16px 24px' }}
        >
          <div className={GROUP}>
            {order.map((item, i) => (
              <FileRow
                key={i}
                item={item}
                index={i}
                isDragOver={dragOverIndex === i}
                isDragging={draggingIndex === i}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex-shrink-0 flex flex-col gap-3 border-t border-[#e6e6e6]"
          style={{ padding: '16px', background: '#F2F2F7' }}
        >
          <button data-testid="video-order-confirm" onClick={() => onConfirm(order)} className={`${BTN_PRIMARY} w-full`}>
            {confirmLabel}
          </button>
          <button data-testid="video-order-cancel" onClick={onCancel} className={`${BTN_SECONDARY} w-full`}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
